export const EVENT_TIME_ZONE_BY_CODE = {
  CT: "America/Chicago",
  ET: "America/New_York",
  MT: "America/Denver",
  PT: "America/Los_Angeles",
} as const;

export type EventTimezoneCode = keyof typeof EVENT_TIME_ZONE_BY_CODE;

export type ParsedEventTime = {
  startMinutes: number;
  durationMinutes: number;
  endMinutes: number;
};

/**
 * Parses the human time labels used by managed events and Zoom scheduling.
 * A single start time keeps the established one-hour fallback.
 */
export function parseEventTimeLabel(timeLabel: string): ParsedEventTime | null {
  const matches = Array.from(
    timeLabel.matchAll(/\b(\d{1,2})(?::(\d{2}))?\s*(a\.?m\.?|p\.?m\.?)\b/gi),
  );
  if (!matches.length) return null;

  const minutes = matches.map(match => {
    const hour = Number(match[1]);
    const minute = match[2] ? Number(match[2]) : 0;
    const meridiem = match[3].replaceAll(".", "").toLowerCase();
    if (hour < 1 || hour > 12 || minute > 59) return null;
    return (hour % 12) * 60 + minute + (meridiem === "pm" ? 12 * 60 : 0);
  });
  if (minutes.some(value => value === null)) return null;

  const startMinutes = minutes[0] as number;
  const explicitEndMinutes = minutes[1] as number | undefined;
  const durationMinutes = explicitEndMinutes === undefined
    ? 60
    : Math.max(
        15,
        Math.min(
          12 * 60,
          (explicitEndMinutes - startMinutes + 24 * 60) % (24 * 60) || 60,
        ),
      );

  return {
    startMinutes,
    durationMinutes,
    endMinutes: startMinutes + durationMinutes,
  };
}

function calendarClock(now: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(now);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find(part => part.type === type)?.value);

  return {
    year: value("year"),
    month: value("month"),
    day: value("day"),
    minutes: value("hour") * 60 + value("minute"),
  };
}

function calendarDayNumber(year: number, month: number, day: number) {
  return Math.floor(Date.UTC(year, month - 1, day) / 86_400_000);
}

/**
 * Whether an event has reached its end time in its advertised US timezone.
 * Unparseable times retain the previous date-only behavior rather than guessing.
 */
export function hasEventEnded(
  event: { date: string; time: string; timezone?: EventTimezoneCode },
  now: Date = new Date(),
): boolean {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(event.date.trim());
  if (!dateMatch) return false;

  const eventDay = calendarDayNumber(
    Number(dateMatch[1]),
    Number(dateMatch[2]),
    Number(dateMatch[3]),
  );
  const clock = calendarClock(
    now,
    EVENT_TIME_ZONE_BY_CODE[event.timezone ?? "CT"],
  );
  const currentDay = calendarDayNumber(clock.year, clock.month, clock.day);
  const timing = parseEventTimeLabel(event.time);

  if (!timing) return currentDay > eventDay;

  const elapsedLocalMinutes = (currentDay - eventDay) * 24 * 60 + clock.minutes;
  return elapsedLocalMinutes >= timing.endMinutes;
}